import React, { useState } from 'react';
import { useIntl, IntlProvider } from 'react-intl';
import { useMutation, ApolloProvider } from '@apollo/client';
import translations from '../../../lib/i18n/translations.json';
import { gql } from '@apollo/client';
import { get } from 'lodash';
import client from '../../../lib/apolloClient';
import SuccessionThemeProvider, { useSuccessionTheme } from '../SuccessionThemeProvider';
import FinancialDetails from './FinancialDetails';
import Description from './Description';
import Details from './Details';
import PriceDetails from './PriceDetails';
import Review from './Review';
import Publish from './Publish';

const CREATE_PARTIAL_SALE = gql`
  mutation createPartialSale(
    $numberOfClients: Int!,
    $assetsUnderManagement: Float!,
    $feeBasedRevenue: Float!,
    $trailerRevenue: Float!,
    $directRevenue: Float!,
    $commissionRevenue: Float!,
    $description: String!,
    $questions: [String!],
    $answers: [Boolean!],
    $states: [String!],
    $clientDemographicsDetails: String,
    $servicesProvidedDetails: String,
    $otherDetails: String,
    $askingPrice: Float!,
    $segmentDetails: String!,
    $publishOnSave: Boolean!) {
    createPartialSale(numberOfClients: $numberOfClients, 
                      assetsUnderManagement: $assetsUnderManagement, 
                      feeBasedRevenue: $feeBasedRevenue, 
                      trailerRevenue: $trailerRevenue, 
                      directRevenue: $directRevenue, 
                      commissionRevenue: $commissionRevenue, 
                      description: $description, 
                      questions: $questions, 
                      answers: $answers,
                      states: $states,
                      clientDemographicsDetails: $clientDemographicsDetails,
                      servicesProvidedDetails: $servicesProvidedDetails,
                      otherDetails: $otherDetails, 
                      askingPrice: $askingPrice, 
                      segmentDetails: $segmentDetails, 
                      publishOnSave: $publishOnSave) {
      id
    }
  }
`;

const UPDATE_CURRENT_USER_PARTIAL_SALE_OPT_IN = gql`
  mutation updateCurrentUserPartialSaleOptIn($id: ID!, $partialSaleVisibilityOptIn: Boolean!, $partialSaleVisibilityOptInDate: String!) {
    updateCurrentUserPartialSaleOptIn(id: $id, partialSaleVisibilityOptIn: $partialSaleVisibilityOptIn, partialSaleVisibilityOptInDate: $partialSaleVisibilityOptInDate){
      user {
        id
        partial_sale_visibility_opt_in
        partial_sale_visibility_opt_in_date
      }
      errors
    }
  }
`;

// Inner component that uses the theme context
const SuccessionPartialSalesWizardContent = ({ partialSale, user, company, partialSaleQuestions, features }) => {
  const intl = useIntl();
  const { theme, isLightTheme, isDarkTheme } = useSuccessionTheme();
  const [createPartialSale] = useMutation(CREATE_PARTIAL_SALE);
  const [updateCurrentUserPartialSaleOptIn] = useMutation(UPDATE_CURRENT_USER_PARTIAL_SALE_OPT_IN);
  const [activeStep, setActiveStep] = useState(0);
  const [checkboxChecked, setCheckboxChecked] = useState(false); 
  const [showWizard, setShowWizard] = useState(false); 
  const [open, setOpen] = useState(false);
  const [stepErrors, setStepErrors] = useState({});

  const userOptIn = get(user, 'partial_sale_visibility_opt_in');
  const successionUser = get(user, 'motivation') === 'succession';
  const skipPopup = successionUser ? userOptIn : true;

  // Check if modernization feature is enabled
  const isModernizationEnabled = features?.includes('findbob_modernization');

  const [formData, setFormData] = useState({
    numberOfClients: '',
    assetsUnderManagement: 0.0,
    feeBasedRevenue: 0.0,
    trailerRevenue: 0.0,
    directRevenue: 0.0,
    commissionRevenue: 0.0,
    description: "",
    states: [],
    clientDemographicsDetails: "",
    servicesProvidedDetails: "",
    otherDetails: "",
    questions: [],
    answers: [],
    askingPrice: 0.0,
    segmentDetails: '',
    publishOnSave: true,
  });

  const partialSalesPropertyCasualty = company?.feature_types?.includes('partial_sales_property_casualty') || false;
  const hideProductSalesPurchasePrice = company?.feature_types?.includes('hide_partial_sales_purchase_price') || false;

  const allSteps = [
    intl.formatMessage({ id: 'succession.partial_sales.wizard.step_financial_details' }),
    intl.formatMessage({ id: 'succession.partial_sales.wizard.step_details' }),
    intl.formatMessage({ id: 'succession.partial_sales.wizard.step_price_details' }),
    intl.formatMessage({ id: 'succession.partial_sales.wizard.step_description' }),
    intl.formatMessage({ id: 'succession.partial_sales.wizard.step_review' }),
    intl.formatMessage({ id: 'succession.partial_sales.wizard.step_publish' }),
  ];

  const priceDetailsLabel = intl.formatMessage({ id: 'succession.partial_sales.wizard.step_price_details' });
  const steps = hideProductSalesPurchasePrice
    ? allSteps.filter(step => step !== priceDetailsLabel)
    : allSteps;

  const handleNext = () => {
    setActiveStep((prevActiveStep) => prevActiveStep + 1);
    // Scroll to top of the wizard
    setTimeout(() => {
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }, 100);
  };

  const handleBack = () => {
    setActiveStep((prevActiveStep) => prevActiveStep - 1);
    // Scroll to top of the wizard
    setTimeout(() => {
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }, 100);
  };

  const handleReset = () => {
    setActiveStep(0);
    setFormData({
      numberOfClients: '',
      assetsUnderManagement: 0.0,
      feeBasedRevenue: 0.0,
      trailerRevenue: 0.0,
      directRevenue: 0.0,
      commissionRevenue: 0.0,
      description: "",
      states: [],
      clientDemographicsDetails: "",
      servicesProvidedDetails: "",
      otherDetails: "",
      questions: [],
      answers: [],
      askingPrice: 0.0,
      segmentDetails: '',
      publishOnSave: true,
    });
  };

  const handleStepClick = (stepIndex) => {
    setActiveStep(stepIndex);
    // Scroll to top of the wizard
    setTimeout(() => {
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }, 100);
  };

  const handleCurrencyChange = (name, value) => {
    setFormData({ ...formData, [name]: value });
  };

  const handleStepValidation = (stepIndex, hasErrors) => {
    setStepErrors(prev => ({
      ...prev,
      [stepIndex]: hasErrors
    }));
  };

  const isCurrentStepValid = () => {
    return !stepErrors[activeStep];
  };

  const handleCheckboxChange = () => {
    setCheckboxChecked(!checkboxChecked);
  };

  const handleSubmitOptIn = async () => {
    try {
      await updateCurrentUserPartialSaleOptIn({
        variables: {
          id: user.id,
          partialSaleVisibilityOptIn: checkboxChecked,
          partialSaleVisibilityOptInDate: new Date().toISOString()
        }
      });
      setOpen(false);
      setShowWizard(true);
    } catch (error) {
      console.error('Error updating opt-in status:', error);
    }
  };

  const getStepContent = (step) => {
    const stepIndex = hideProductSalesPurchasePrice && step >= 2 ? step + 1 : step;
    
    switch (stepIndex) {
      case 0:
        return (
          <FinancialDetails
            formData={formData}
            setFormData={setFormData}
            handleCurrencyChange={handleCurrencyChange}
            company={company}
            partialSalesPropertyCasualty={partialSalesPropertyCasualty}
            user={user}
            onValidationChange={(hasErrors) => handleStepValidation(0, hasErrors)}
          />
        );
      case 1:
        return (
          <Details
            formData={formData}
            setFormData={setFormData}
            partialSaleQuestions={partialSaleQuestions}
            company={company}
            onValidationChange={(hasErrors) => handleStepValidation(1, hasErrors)}
          />
        );
      case 2:
        return (
          <PriceDetails
            formData={formData}
            setFormData={setFormData}
            handleCurrencyChange={handleCurrencyChange}
            company={company}
            onValidationChange={(hasErrors) => handleStepValidation(2, hasErrors)}
          />
        );
      case 3:
        return (
          <Description
            formData={formData}
            setFormData={setFormData}
            user={user}
            company={company}
            onValidationChange={(hasErrors) => handleStepValidation(3, hasErrors)}
          />
        );
      case 4:
        return (
          <Review
            formData={formData}
            company={company}
            partialSaleQuestions={partialSaleQuestions}
            partialSalesPropertyCasualty={partialSalesPropertyCasualty}
            hideProductSalesPurchasePrice={hideProductSalesPurchasePrice}
            setCurrentStep={setCurrentStep}
          />
        );
      case 5:
        return (
          <Publish
            formData={formData}
            setFormData={setFormData}
            createPartialSale={createPartialSale}
            company={company}
            user={user}
            partialSaleQuestions={partialSaleQuestions}
          />
        );
      default:
        return <div>{intl.formatMessage({ id: 'succession.partial_sales.wizard.unknown_step' })}</div>;
    }
  };

  if (!skipPopup && !showWizard) {
    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
        <div className="bg-white rounded-lg p-6 max-w-md w-full mx-4">
          <h2 className="text-xl font-semibold mb-4">{intl.formatMessage({ id: 'succession.partial_sales.wizard.opt_in_title' })}</h2>
          <p className="text-gray-600 mb-4">
            {intl.formatMessage({ id: 'succession.partial_sales.wizard.opt_in_body_1' })}
          </p>
          <p className="text-gray-600 mb-4">
            {intl.formatMessage({ id: 'succession.partial_sales.wizard.opt_in_body_2' })}
          </p>
          <div className="flex items-center mb-4">
            <input
              type="checkbox"
              id="opt-in-checkbox"
              checked={checkboxChecked}
              onChange={handleCheckboxChange}
              className="mr-2 h-4 w-4 border-succession-border rounded focus:ring-2 focus:ring-succession-primary focus:ring-offset-2 hover:border-succession-primary transition-colors"
              style={{
                accentColor: 'var(--succession-primary)',
                backgroundColor: checkboxChecked ? 'var(--succession-primary)' : 'transparent'
              }}
            />
            <label htmlFor="opt-in-checkbox" className="text-sm text-succession-text-primary">
              {intl.formatMessage({ id: 'succession.partial_sales.wizard.opt_in_checkbox' })}
            </label>
          </div>
          <div className="flex justify-end space-x-3">
            <button
              onClick={() => setOpen(false)}
              className="px-4 py-2 bg-succession-secondary text-white rounded hover:opacity-80 transition-all"
            >
              {intl.formatMessage({ id: 'succession.partial_sales.wizard.opt_in_cancel' })}
            </button>
            <button
              onClick={handleSubmitOptIn}
              className="px-4 py-2 bg-succession-primary text-white rounded hover:opacity-80 transition-all"
            >
              {intl.formatMessage({ id: 'succession.partial_sales.wizard.opt_in_continue' })}
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-succession-bg-secondary transition-colors duration-300">
      <div className="max-w-4xl mx-auto py-8 px-4">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-3xl font-bold mb-2 text-succession-text-primary transition-colors duration-300">
            {intl.formatMessage({ id: 'succession.partial_sales.wizard.header_title' })}
          </h1>
          <p className="text-lg text-succession-text-secondary transition-colors duration-300">
            {intl.formatMessage({ id: 'succession.partial_sales.wizard.header_subtitle' })}
          </p>
        </div>

        {/* Progress Stepper */}
        <div className="mb-8">
          <div className="flex items-center">
            {steps.map((label, index) => (
              <div key={label} className="flex items-center">
                <div
                  className={`flex items-center justify-center w-10 h-10 rounded-full border-2 cursor-pointer transition-colors ${
                    index <= activeStep
                      ? 'bg-succession-primary border-succession-primary text-white'
                      : 'border-succession-border text-succession-text-muted hover:border-succession-primary'
                  }`}
                  onClick={() => handleStepClick(index)}
                >
                  {index < activeStep ? (
                    <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                    </svg>
                  ) : (
                    <span className="text-sm font-semibold">{index + 1}</span>
                  )}
                </div>
                <span
                  className={`ml-2 text-sm font-medium ${
                    index <= activeStep ? 'text-succession-primary' : 'text-succession-text-muted'
                  }`}
                >
                  {label}
                </span>
                {index < steps.length - 1 && (
                  <div
                    className={`flex-1 h-0.5 mx-4 ${
                      index < activeStep ? 'bg-succession-primary' : 'bg-succession-border'
                    }`}
                  />
                )}
              </div>
            ))}
          </div>
        </div>

        {/* Step Content */}
        <div className="bg-white rounded-lg shadow-sm border p-6 border-succession-border transition-colors duration-300">
          {getStepContent(activeStep)}
        </div>

        {/* Navigation Buttons */}
        <div className="mt-8 flex justify-between">
          <button
            onClick={handleBack}
            disabled={activeStep === 0}
            className={`px-6 py-2 rounded-lg font-medium transition-all ${
              activeStep === 0
                ? 'bg-succession-bg-secondary text-succession-text-muted cursor-not-allowed border border-succession-border'
                : 'bg-succession-secondary text-white hover:opacity-80'
            }`}
          >
            {intl.formatMessage({ id: 'succession.partial_sales.wizard.back' })}
          </button>
          <div className="flex space-x-3">
            <button
              onClick={handleReset}
              className="px-6 py-2 border border-succession-border text-succession-text-secondary rounded-lg hover:bg-succession-bg-secondary transition-colors"
            >
              {intl.formatMessage({ id: 'succession.partial_sales.wizard.reset' })}
            </button>
            {activeStep < steps.length - 1 && (
              <button
                onClick={handleNext}
                disabled={!isCurrentStepValid()}
                className={`px-6 py-2 rounded-lg font-medium transition-colors ${
                  isCurrentStepValid()
                    ? 'bg-succession-primary text-white hover:opacity-80 transition-all'
                    : 'bg-succession-bg-secondary text-succession-text-muted cursor-not-allowed border border-succession-border'
                }`}
              >
                {intl.formatMessage({ id: 'succession.partial_sales.wizard.next' })}
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

// Wrapper component with Apollo and theme providers
const SuccessionPartialSalesWizard = (props, railsContext) => {
  window.locale = (railsContext && railsContext.i18nLocale) || window.locale || 'en';
  const locale = window.locale;
  return (
    <ApolloProvider client={client}>
      <IntlProvider locale={locale} messages={translations[locale] || translations.en}>
        <SuccessionThemeProvider company={props.company} user={props.user}>
          <SuccessionPartialSalesWizardContent {...props} />
        </SuccessionThemeProvider>
      </IntlProvider>
    </ApolloProvider>
  );
};

export default SuccessionPartialSalesWizard;